import { Card } from '@components'
import { useService } from 'solid-services'
import { throttle } from '@solid-primitives/scheduled'
import { useNavigate } from '@solidjs/router'
import { userId } from '@state'
import { For, createEffect, createSignal, on, onCleanup } from 'solid-js'
import { Box } from 'styled-system/jsx'
import { useChannel, useDelete, useUi, useUpsert } from '../state'
import Item from './Item'
import MessageActions from './MessageActions'

export function Messages(props: {isMinimal?: boolean}) {
	const navigate = useNavigate()
	const {
		updateEmojiForMessage,
		messages,
		removeMessage,
		paginatedChannelHistory,
		focusMessageId,
		refetch,
		scrollTo,
		chatMode,
		loadNextMessages,
		loadPreviousMessages,
		isOldestLoaded,
		isLatestLoaded,
	} = useService(useChannel)()
	const { upsertMessageReactionResource } = useService(useUpsert)()
	const { deletingResource, deletingId } = useService(useDelete)()
	const { channelPathRoot, setNotAtBottom, ready, setReady } = useService(useUi)()
	const [container, setContainer] = createSignal<Element | null>(null)
	const [postPrevIndex, setPostPrevIndex] = createSignal<string | null>(null)
	const [loadNextIndex, setLoadNextIndex] = createSignal<string | null>(null)

	const throttledLoadPrevious = throttle(loadPreviousMessages, 1000)
	const throttledLoadNext = throttle(loadNextMessages, 200)

	// Scroll tracking
	const [scrollTick, setScrollTick] = createSignal(0)

	createEffect(() => {
		const el = container()
		if (!el) return

		const onScroll = () => setScrollTick((t) => t + 1)
		el.addEventListener('scroll', onScroll, { passive: true })

		onCleanup(() => {
			el.removeEventListener('scroll', onScroll)
		})
	})

	// Pagination logic (bidirectional)
	createEffect(
		on(scrollTick, () => {
			scrollTick()

			const el = container()
			if (!el || messages.length === 0) return
			if (!ready()) return

			const containerRect = el.getBoundingClientRect()
			const first = document.getElementById(messages[0]?.id)
			const secondLast = document.getElementById(messages[messages.length - 2]?.id)
			const last = document.getElementById(messages[messages.length - 1]?.id)

			if (first && !isOldestLoaded()) {
				const rect = first.getBoundingClientRect()
				if (rect.top >= containerRect.top && rect.bottom <= containerRect.bottom) {
					console.log(first.id)
					setPostPrevIndex(first.id)
					throttledLoadPrevious()
				}
			}

			if (secondLast) {
				const rect = secondLast.getBoundingClientRect()
				const isVisible =
					rect.top >= containerRect.top && rect.bottom <= containerRect.bottom
				setNotAtBottom(!isVisible)
			}

			if (last && !isLatestLoaded()) {
				const rect = last.getBoundingClientRect()
				const isVisible =
					rect.top >= containerRect.top && rect.bottom <= containerRect.bottom
				if (isVisible) {
					setLoadNextIndex(last.id)
					throttledLoadNext()
				}
			}
		}),
	)

	// Scroll to message (from scrollTo signal)
	createEffect(() => {
		const target = scrollTo()
		if (target?.id) {
			setTimeout(() => {
				const el = document.getElementById(target.id)
				if (el)
					el.scrollIntoView({
						behavior: (target as any).behavior,
						block: (target as any).align as any,
					})
			}, 400)
		}
	})

	// Scroll to post-pagination message (older)
	createEffect(() => {
		if (paginatedChannelHistory.state === 'ready' && postPrevIndex()) {
			setTimeout(() => {
				const el = document.getElementById(postPrevIndex()!)
				console.log(postPrevIndex())
				if (el) el.scrollIntoView({ block: 'start' })
				setPostPrevIndex(null)
			}, 800)
		}
	})

	// Scroll to post-pagination message (newer)
	createEffect(() => {
		if (paginatedChannelHistory.state === 'ready' && loadNextIndex()) {
			setTimeout(() => {
				const el = document.getElementById(loadNextIndex()!)
				if (el) el.scrollIntoView({ block: 'end' })
				setLoadNextIndex(null)
			}, 400)
		}
	})

	// Scroll to bottom on initial load
	createEffect(() => {
		if (ready()) return
		if (messages.length && !focusMessageId()) {
			setTimeout(() => {
				const last = document.getElementById(messages[messages.length - 1]?.id)
				if (last) last.scrollIntoView({ block: 'end' })
				setReady(true)
			}, 400)
		}
	})

	// Scroll to focus message on first render
	createEffect(() => {
		if (ready()) return
		if (messages.length && focusMessageId()) {
			const id = focusMessageId()
			setTimeout(() => {
				const el = document.getElementById(id!)
				if (el) el.scrollIntoView({ block: 'center' })
				setReady(true)
			}, 400)
		}
	})

	createEffect(() => {
		if (deletingResource.state === 'ready' && deletingId()) {
			removeMessage(deletingId()!)
		}
	})

	createEffect(() => {
		if (upsertMessageReactionResource.state === 'ready') {
			updateEmojiForMessage(upsertMessageReactionResource())
		}
	})

	const scrollToBottom = () => {
		if (chatMode() === 'history') {
			setReady(false)
			navigate(channelPathRoot())
			refetch()
		} else {
			const last = document.getElementById(messages[messages.length - 1]?.id)
			if (last) last.scrollIntoView({ block: 'end' })
		}
	}

	return (
		<>
			<MessageActions scrollToBottom={scrollToBottom} />
			<Card.Root
				display="block"
				overflowY="auto"
				ref={setContainer}
			>
				<Box
					width="100%"
					padding={4}
					flexDirection="column"
				>
					<For each={messages}>
						{(message, index) => {
							const nextMessage = messages[index() + 1]
							const prevMessage = messages[index() - 1]
							const isNextBySameAuthor = nextMessage?.created_by === message.created_by
							const isNextByDifferentAuthor =
								nextMessage?.created_by !== message.created_by
							const isPrevByDifferentAuthor =
								prevMessage?.created_by !== message.created_by
							const showMessageAuthor =
								(isNextBySameAuthor && isPrevByDifferentAuthor) ||
								(isPrevByDifferentAuthor && nextMessage === undefined) ||
								(isPrevByDifferentAuthor && isNextByDifferentAuthor)
							const isFocusingMessage = focusMessageId() === message.id
							const isHighlightMessage =
								scrollTo()?.id === message.id && scrollTo()?.isHighlight

							return (
								// <div id={String(message.id)}>
								<Item
                  isMinimal={props?.isMinimal}
									isFocus={isFocusingMessage || isHighlightMessage}
									message={message}
									isMine={message.created_by === userId()}
									showMessageAuthor={showMessageAuthor}
								/>
								// </div>
							)
						}}
					</For>
				</Box>
			</Card.Root>
		</>
	)
}
